import { AutosizeTextarea, AutosizeTextAreaRef, Button } from '@quadminds/mindkit';
import { ArrowUp, Square } from 'lucide-react';
import * as React from 'react';
import { forwardRef, Ref, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import ModeSelector from './ModeSelector/ModeSelector';
import { UploadDestination } from '../../../../types/File/File.types';

export interface UploadedFile {
    name: string;
    size: number;
    type: string;
    preview: string;
    file: File;
}

interface InputProps {
    chatInputValue: string;
    setChatInputValue: React.Dispatch<React.SetStateAction<string>>;
    files: UploadedFile[];
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
    onSendMessage: () => void;
    onCancel?: () => void;
    canCancel?: boolean;
    isLoading: boolean;
    isStreaming?: boolean;
    isDifferentAgentVersion: boolean;
    isModeLocked: () => boolean;
    showKnowledgeEmbeddingSelector: boolean;
    knowledgeEmbeddingDestination: UploadDestination;
    setKnowledgeEmbeddingDestination: React.Dispatch<React.SetStateAction<UploadDestination>>;
}

export const Input = forwardRef(
    (
        {
            chatInputValue,
            setChatInputValue,
            files,
            onSendMessage,
            onCancel,
            canCancel = false,
            isLoading = false,
            isStreaming = false,
            isDifferentAgentVersion,
            isModeLocked,
            showKnowledgeEmbeddingSelector,
            knowledgeEmbeddingDestination,
            setKnowledgeEmbeddingDestination,
        }: InputProps,
        ref: Ref<{ focusTextArea(): void; isTextAreaFocused(): boolean; isTextAreaDisabled(): boolean }>,
    ) => {
        const textAreaRef = React.useRef<AutosizeTextAreaRef | null>(null);
        const { t } = useTranslation();

        useImperativeHandle(ref, () => ({
            isTextAreaDisabled: () => textAreaRef.current?.textArea.disabled ?? false,
            isTextAreaFocused: () => textAreaRef.current?.textArea === document.activeElement,
            focusTextArea: () => {
                textAreaRef.current?.textArea.focus();
            },
        }));

        return (
            <div
                className={`
            relative flex flex-col space-y-1 border p-1 rounded-2xl
            border-gray-qm-300 
            dark:border-gray-qm-600 dark:border-0 dark:bg-gray-qm-900
        `}
            >
                <input className="hidden" />
                <div className="flex items-start justify-between gap-2 pr-2">
                    <AutosizeTextarea
                        autoFocus
                        ref={textAreaRef}
                        placeholder={
                            isDifferentAgentVersion
                                ? 'Esta conversación utiliza una versión de modelo diferente. Por favor, inicie una nueva conversación para usar el modelo actual.'
                                : t('chat.inputPlaceholder')
                        }
                        maxHeight={200}
                        minHeight={isDifferentAgentVersion ? 100 : 28}
                        className={`${
                            isDifferentAgentVersion ? 'text-sm' : 'text-lg'
                        } border-0 focus-visible:!ring-0 focus-visible:!ring-offset-0 !bg-transparent ${
                            isDifferentAgentVersion ? 'items-center justify-center flex' : 'items-center'
                        } px-3 py-2 ${
                            isDifferentAgentVersion ? 'min-h-[100px]' : 'min-h-[40px]'
                        } max-h-[200px] resize-none flex-1 disabled:opacity-50 disabled:cursor-not-allowed scrollbar-thin scrollbar-thumb-gray-qm-50 dark:scrollbar-thumb-gray-qm-800 scrollbar-track-transparent scrollbar-thumb-rounded-full pt-3 pl-4`}
                        value={chatInputValue}
                        onChange={(e) => setChatInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (chatInputValue.trim().length >= 1) {
                                    onSendMessage();
                                }
                            }
                        }}
                        disabled={isLoading || isStreaming || isDifferentAgentVersion}
                    />
                    {isStreaming || canCancel ? (
                        <Button
                            type="button"
                            className="rounded-full dark:bg-white dark:text-primary hover:dark:bg-tertiary-qm-100 !h-10 !w-10 !min-w-10 mt-1"
                            size="icon"
                            onClick={onCancel}
                            data-testid="cancel-button"
                            title={t('chat.cancelStream')}
                        >
                            <Square className="fill-current" />
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            className="rounded-full dark:bg-white dark:text-primary hover:dark:bg-tertiary-qm-100 !h-10 !w-10 !min-w-10 mt-1"
                            size="icon"
                            disabled={isLoading || chatInputValue.trim().length < 1 || isDifferentAgentVersion}
                            onClick={onSendMessage}
                            data-testid="send-button"
                        >
                            <ArrowUp />
                        </Button>
                    )}
                </div>
                {showKnowledgeEmbeddingSelector && (
                    <div className="flex items-center gap-2 px-2 pb-1">
                        <span className="text-xs text-muted-foreground">Embedding:</span>
                        <select
                            className="rounded-md border border-gray-qm-300 bg-transparent text-xs px-2 py-1 dark:border-gray-qm-700"
                            value={knowledgeEmbeddingDestination}
                            onChange={(e) => setKnowledgeEmbeddingDestination(e.target.value as UploadDestination)}
                            data-testid="knowledge-embedding-selector"
                        >
                            <option value={UploadDestination.STANDARD}>{UploadDestination.STANDARD}</option>
                            <option value={UploadDestination.BETA}>{UploadDestination.BETA}</option>
                        </select>
                    </div>
                )}
                <ModeSelector isModeLocked={isModeLocked} />
            </div>
        );
    },
);

export default Input;
